import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { AlertCircle } from 'lucide-react';

import { useScope } from '@workspace/scope-context';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@workspace/ui';

import AccountWorkspaceTabs from '@/components/account/AccountWorkspaceTabs';
import ActiveAccountPrompt from '@/components/account/ActiveAccountPrompt';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigation } from '@/context/NavigationContext';
import { listAccountTenants, setActiveAccount, type AccountTenantRecord } from '@/lib/accounts';
import { buildAccountRoute, parseAccountRoute } from '@/lib/account-routing';
import {
  fetchMyAccountSummary,
  formatCurrency,
  formatDate,
  type MyAccountSummary
} from '@/lib/my-account';

export default function AccountOverviewPage() {
  const { path, navigate } = useNavigation();
  const { activeTenant } = useScope();

  const accountId = useMemo(() => parseAccountRoute(path).accountId, [path]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    void setActiveAccount(accountId).catch(() => undefined);
  }, [accountId]);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError
  } = useSWR<MyAccountSummary>(
    accountId ? ['/api/v1/my-account/summary', accountId] : null,
    () => fetchMyAccountSummary(accountId ?? undefined)
  );

  const {
    data: tenants = [],
    isLoading: tenantsLoading,
    error: tenantsError
  } = useSWR<AccountTenantRecord[]>(
    accountId ? ['account-tenants', accountId] : null,
    ([, id]: [string, string]) => listAccountTenants(id)
  );

  if (!accountId) {
    return <ActiveAccountPrompt />;
  }

  const accountName = summary?.account?.name ?? accountId;
  const legacyCustomerId = summary?.account?.legacyCustomerId ?? null;
  const lastPaymentDate = summary?.lastPayment?.postedAt ? new Date(summary.lastPayment.postedAt) : null;
  const hasError = summaryError || tenantsError;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <AccountWorkspaceTabs accountId={accountId} current="overview" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <CardTitle>{summaryLoading ? <Skeleton className="h-6 w-56" /> : accountName}</CardTitle>
              <CardDescription>
                {summaryLoading ? (
                  <Skeleton className="h-4 w-40" />
                ) : legacyCustomerId ? (
                  <>Legacy customer ID · {legacyCustomerId}</>
                ) : (
                  'Legacy customer ID unavailable'
                )}
              </CardDescription>
            </div>
            {activeTenant ? <Badge variant="secondary">{activeTenant.name ?? activeTenant.id}</Badge> : null}
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button type="button" onClick={() => navigate(buildAccountRoute(accountId, 'billing'))}>
            Pay bill
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/support')}>
            Open support
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(buildAccountRoute(accountId, 'services'))}>
            View services
          </Button>
        </CardContent>
      </Card>

      {hasError ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" aria-hidden />
              Unable to load account details
            </CardTitle>
            <CardDescription>{summaryError?.message ?? tenantsError?.message}</CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryTile
          label="Services"
          value={summaryLoading ? '…' : String(summary?.services?.count ?? 0)}
          helper="Active services"
        />
        <SummaryTile
          label="Legacy tickets"
          value={summaryLoading ? '…' : String(summary?.tickets?.openCount ?? 0)}
          helper={`Recently closed: ${summary?.tickets?.recentClosedCount ?? 0}`}
        />
        <SummaryTile
          label="Current balance"
          value={summaryLoading ? '…' : formatCurrency(summary?.billing?.currentBalance ?? summary?.billing?.totalBalance)}
          helper="Updated from billing summary"
        />
        <SummaryTile
          label="Last payment"
          value={summaryLoading ? '…' : formatCurrency(summary?.lastPayment?.amount)}
          helper={summaryLoading ? '—' : formatDate(lastPaymentDate)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tenant associations</CardTitle>
          <CardDescription>
            Tenants linked to this account within your current scope. The active tenant, if any, is highlighted.
          </CardDescription>
        </CardHeader>
        {tenantsLoading ? (
          <CardContent className="px-6 py-10 text-center text-sm text-muted-foreground">Loading tenants…</CardContent>
        ) : tenants.length === 0 ? (
          <CardContent className="px-6 py-10 text-center text-sm text-muted-foreground">
            No tenants were found for this account in your current scope.
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Tenant ID</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map(tenant => {
                  const isActive = activeTenant?.id === tenant.id;
                  return (
                    <TableRow key={tenant.id} className={isActive ? 'bg-primary/5' : undefined}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{tenant.name}</span>
                          {isActive ? (
                            <Badge variant="success" className="text-xs">
                              Active tenant
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-xs text-muted-foreground">{tenant.id}</div>
                      </TableCell>
                      <TableCell>
                        {tenant.region ? <Badge variant="outline">{tenant.region}</Badge> : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {tenant.status ? <Badge variant="secondary">{tenant.status}</Badge> : <span className="text-sm text-muted-foreground">Unknown</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function SummaryTile({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">{helper}</CardContent>
    </Card>
  );
}
